import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@nextui-org/react";
import { Canvas, FabricImage, Rect } from "fabric";
import type { FileWithStatus } from "./uploader.types";

interface ImageEditorModalProps {
  isOpen: boolean;
  fileStatus: FileWithStatus | null;
  canvasSize?: { width: number; height: number };
  onCancel: () => void;
  onSave: (blob: Blob) => void;
}

interface FabricCanvasEditor {
  canvas: Canvas | null;
  image: FabricImage | null;
}

const ImageEditorModal: React.FC<ImageEditorModalProps> = ({
  isOpen,
  fileStatus,
  canvasSize,
  onCancel,
  onSave,
}) => {
  const [fabricEditor, setFabricEditor] = useState<FabricCanvasEditor>({
    canvas: null,
    image: null,
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const initializeFabricCanvas = useCallback(() => {
    if (!canvasRef.current || !canvasSize) return null;

    if (fabricEditor.canvas) {
      fabricEditor.canvas.dispose();
    }

    const canvas = new Canvas(canvasRef.current, {
      width: canvasSize.width,
      height: canvasSize.height,
      backgroundColor: "#f0f0f0",
      selection: true,
    });

    const border = new Rect({
      left: 0,
      top: 0,
      width: canvasSize.width,
      height: canvasSize.height,
      fill: "transparent",
      stroke: "#ddd",
      strokeWidth: 2,
      selectable: false,
      evented: false,
    });
    canvas.add(border);

    setFabricEditor((prev) => ({ ...prev, canvas }));

    return canvas;
  }, [canvasSize, fabricEditor.canvas]);

  const loadImageToCanvas = useCallback(
    (file: File, canvas: Canvas) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imgUrl = e.target?.result as string;

        FabricImage.fromURL(imgUrl)
          .then((img) => {
            const objects = canvas.getObjects();
            objects.forEach((obj) => {
              if (obj.type !== "rect") {
                canvas.remove(obj);
              }
            });

            const scaleX = (canvasSize?.width || 1) / (img.width || 1);
            const scaleY = (canvasSize?.height || 1) / (img.height || 1);
            const scale = Math.min(scaleX, scaleY);

            img.set({
              left: (canvasSize?.width || 0) / 2,
              top: (canvasSize?.height || 0) / 2,
              originX: "center",
              originY: "center",
              scaleX: scale,
              scaleY: scale,
            });

            img.setControlsVisibility({
              mt: true,
              mb: true,
              ml: true,
              mr: true,
              tl: true,
              tr: true,
              bl: true,
              br: true,
              mtr: true,
            });

            canvas.add(img);
            canvas.setActiveObject(img);
            canvas.renderAll();

            setFabricEditor((prev) => ({ ...prev, image: img }));
          })
          .catch(() => {});
      };
      reader.readAsDataURL(file);
    },
    [canvasSize]
  );

  const exportCanvasImage = useCallback((canvas: Canvas): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const objects = canvas.getObjects();
      const border = objects.find((obj) => obj.type === "rect");
      if (border) {
        border.set("visible", false);
      }
      canvas.renderAll();

      try {
        const canvasElement = canvas.getElement();
        canvasElement.toBlob((blob: Blob | null) => {
          if (border) {
            border.set("visible", true);
          }
          canvas.renderAll();

          if (blob) resolve(blob);
          else reject(new Error("Failed to export canvas"));
        });
      } catch (error) {
        try {
          const dataURL = canvas.toDataURL({
            format: "jpeg",
            quality: 0.9,
            multiplier: 1,
          });
          fetch(dataURL)
            .then((res) => res.blob())
            .then((blob: Blob) => {
              if (border) border.set("visible", true);
              canvas.renderAll();
              resolve(blob);
            })
            .catch(reject);
        } catch (fallbackError) {
          reject(fallbackError);
        }
      }
    });
  }, []);

  const resetCanvasView = () => {
    if (fabricEditor.canvas && fabricEditor.image && canvasSize) {
      const scaleX = canvasSize.width / (fabricEditor.image.width || 1);
      const scaleY = canvasSize.height / (fabricEditor.image.height || 1);
      const scale = Math.min(scaleX, scaleY);

      fabricEditor.image.set({
        left: canvasSize.width / 2,
        top: canvasSize.height / 2,
        originX: "center",
        originY: "center",
        scaleX: scale,
        scaleY: scale,
        angle: 0,
      });

      fabricEditor.canvas.renderAll();
    }
  };

  const fitToCanvas = () => {
    if (fabricEditor.canvas && fabricEditor.image && canvasSize) {
      fabricEditor.image.set({
        left: canvasSize.width / 2,
        top: canvasSize.height / 2,
        originX: "center",
        originY: "center",
        scaleX: canvasSize.width / (fabricEditor.image.width || 1),
        scaleY: canvasSize.height / (fabricEditor.image.height || 1),
        angle: 0,
      });

      fabricEditor.canvas.renderAll();
    }
  };

  const handleSave = async () => {
    if (!fabricEditor.canvas) return;
    const blob = await exportCanvasImage(fabricEditor.canvas);
    onSave(blob);
    if (fabricEditor.canvas) fabricEditor.canvas.dispose();
    setFabricEditor({ canvas: null, image: null });
  };

  useEffect(() => {
    if (!isOpen || !fileStatus || !canvasSize) return;
    const canvas = initializeFabricCanvas();
    if (canvas) loadImageToCanvas(fileStatus.file, canvas);
    // cleanup on modal close
    return () => {
      if (fabricEditor.canvas) fabricEditor.canvas.dispose();
      setFabricEditor({ canvas: null, image: null });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fileStatus, canvasSize]);

  if (!canvasSize) return null;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onCancel}
      size="5xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          Edit Image - {fileStatus?.file.name}
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col items-center gap-4">
            <div className="text-sm text-gray-600 mb-2">
              Canvas Size: {canvasSize.width} × {canvasSize.height}px
            </div>
            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <canvas ref={canvasRef} />
            </div>
            <div className="flex gap-2 flex-wrap justify-center">
              <Button
                size="sm"
                variant="light"
                onClick={resetCanvasView}
                disabled={!fabricEditor.image}
              >
                Reset View
              </Button>
              <Button
                size="sm"
                variant="light"
                onClick={fitToCanvas}
                disabled={!fabricEditor.image}
              >
                Fit to Canvas
              </Button>
            </div>
            <div className="text-xs text-gray-500 text-center max-w-md">
              Use the corner handles to resize, drag to move, or use the
              rotation handle at the top to rotate the image. The final image
              will be {canvasSize.width} × {canvasSize.height}px.
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="default" variant="light" onPress={onCancel}>
            Cancel
          </Button>
          <Button color="primary" onPress={handleSave}>
            Save & Upload
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImageEditorModal;
